/**
 * Direct Taxonomy Loader
 * Reads from RuleDoc-Drill-Taxonomy.json and localStorage
 * Auto-merges JSON file changes with localStorage customizations
 * No intermediate static fallbacks - ensures data consistency
 */

import { loadTagTree, writeTaxonomyToStorage, readTaxonomyFromStorage } from '@/lib/tag-system/json-handler';

// Cache with timestamp validation
let cachedTaxonomy: any = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Key to track which JSON file version was last merged
const MERGED_VERSION_KEY = 'drillTaxonomy_mergedVersion';

// Flag to ensure merge only happens once per session
let mergeCheckedThisSession = false;

export interface TaxonomyData {
  version: string;
  lastModified: string;
  defaults?: {
    colorizationCategory?: string;
  };
  categories: {
    [key: string]: {
      type: string;
      description?: string;
      displayLabel?: string;
      options: string[];
    };
  };
  subcategoryMap?: {
    [key: string]: string[];
  };
  programMethodology?: {
    [key: string]: string;
  };
  colors: {
    categoryLevelColors: {
      [key: string]: string;
    };
    optionColors: {
      [key: string]: string;
    };
  };
}
import logger from '@/lib/logging';

/**
 * Load full taxonomy from RuleDoc-Drill-Taxonomy.json
 * Auto-merges JSON file changes with localStorage customizations
 * Checks version on every cache expiration to detect updates
 * 
 * @returns Complete taxonomy data with all categories, colors, and metadata
 * @throws Error if taxonomy cannot be loaded from any source
 */
export async function loadTaxonomy(): Promise<TaxonomyData> {
  const now = Date.now();

  // Return cached if still valid
  if (cachedTaxonomy && now - cacheTimestamp < CACHE_DURATION) {
    return cachedTaxonomy;
  }

  try {
    // Load the canonical JSON file
    const module = await import('@/config/RuleDoc-Drill-Taxonomy.json');
    const jsonFileTaxonomy = module.default as unknown as TaxonomyData;
    
    // Check if localStorage has data
    const storedData = readTaxonomyFromStorage();
    
    if (!storedData) {
      // No localStorage data - use JSON file directly
      logger.debug('📂 No localStorage taxonomy found, using JSON file');
      cachedTaxonomy = jsonFileTaxonomy;
      cacheTimestamp = now;
      saveMergedVersion(jsonFileTaxonomy.version);
      return jsonFileTaxonomy;
    }
    
    // Parse localStorage data
    let localTaxonomy: TaxonomyData;
    try {
      localTaxonomy = JSON.parse(storedData);
    } catch (e) {
      logger.warn('Failed to parse localStorage taxonomy, using JSON file');
      cachedTaxonomy = jsonFileTaxonomy;
      cacheTimestamp = now;
      saveMergedVersion(jsonFileTaxonomy.version);
      return jsonFileTaxonomy;
    }
    
    // Check if we need to merge - check version whenever cache expires
    // This ensures we detect taxonomy updates even if they happen during the session
    const lastMergedVersion = getLastMergedVersion();
    const currentJsonVersion = jsonFileTaxonomy.version;
    
    if (lastMergedVersion !== currentJsonVersion) {
      logger.debug(`🔄 Taxonomy JSON file updated (${lastMergedVersion || 'unknown'} → ${currentJsonVersion}), replacing with file...`);
      
      // Replace localStorage with JSON file - JSON file is source of truth for taxonomy structure
      writeTaxonomyToStorage(JSON.stringify(jsonFileTaxonomy, null, 2));
      saveMergedVersion(currentJsonVersion);
      
      logger.debug('✅ Taxonomy replaced from JSON file');
      
      cachedTaxonomy = jsonFileTaxonomy;
      cacheTimestamp = now;
      mergeCheckedThisSession = true; // Mark as checked for this session
      return jsonFileTaxonomy;
    }
    
    // Mark as checked for this session if we haven't already
    if (!mergeCheckedThisSession) {
      mergeCheckedThisSession = true;
    }
    
    // Validate localStorage taxonomy
    if (!localTaxonomy || typeof localTaxonomy !== 'object') {
      throw new Error('Taxonomy loaded but invalid structure');
    }

    if (!localTaxonomy.categories || Object.keys(localTaxonomy.categories).length === 0) {
      logger.warn('Loaded taxonomy has no categories, using JSON file');
      cachedTaxonomy = jsonFileTaxonomy;
      cacheTimestamp = now;
      return jsonFileTaxonomy;
    }

    // Ensure colors exist
    if (!localTaxonomy.colors) {
      localTaxonomy.colors = { categoryLevelColors: {}, optionColors: {} };
    }

    cachedTaxonomy = localTaxonomy;
    cacheTimestamp = now;
    return localTaxonomy;
  } catch (e) {
    logger.error('Failed to load taxonomy, trying JSON file directly', e);

    // Final fallback: load directly from JSON file
    try {
      const module = await import('@/config/RuleDoc-Drill-Taxonomy.json');
      const json = module.default as unknown as TaxonomyData;
      cachedTaxonomy = json;
      cacheTimestamp = now;
      return json;
    } catch (importError) {
      logger.error('Failed to load taxonomy from JSON file', importError);
      throw new Error('Could not load taxonomy from any source');
    }
  }
}

/**
 * Get the last merged JSON file version from localStorage
 * Exported for use in startup checks
 */
export function getLastMergedVersion(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(MERGED_VERSION_KEY);
  } catch {
    return null;
  }
}

/**
 * Save the merged version to localStorage
 */
function saveMergedVersion(version: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(MERGED_VERSION_KEY, version);
  } catch (e) {
    logger.error('Failed to save merged version:', e);
  }
}

/**
 * Merge two taxonomies - keeps options from BOTH, preserving localStorage customizations
 * Rule: Union of all categories and options from both sources
 */
function mergeTaxonomies(local: TaxonomyData, jsonFile: TaxonomyData): TaxonomyData {
  const merged: TaxonomyData = {
    version: jsonFile.version, // Use JSON file version
    lastModified: new Date().toISOString(),
    defaults: jsonFile.defaults || local.defaults,
    categories: {},
    subcategoryMap: { ...local.subcategoryMap, ...jsonFile.subcategoryMap },
    programMethodology: jsonFile.programMethodology || local.programMethodology,
    colors: {
      categoryLevelColors: { ...local.colors?.categoryLevelColors, ...jsonFile.colors?.categoryLevelColors },
      optionColors: { ...local.colors?.optionColors, ...jsonFile.colors?.optionColors }
    }
  };
  
  // Get all category names from both sources
  const allCategories = new Set([
    ...Object.keys(local.categories || {}),
    ...Object.keys(jsonFile.categories || {})
  ]);
  
  for (const categoryName of allCategories) {
    const localCat = local.categories?.[categoryName];
    const jsonCat = jsonFile.categories?.[categoryName];
    
    if (localCat && jsonCat) {
      // Category exists in both - merge options (union)
      const localOptions = new Set(localCat.options || []);
      const jsonOptions = new Set(jsonCat.options || []);
      const mergedOptions = [...new Set([...localOptions, ...jsonOptions])];
      
      // Log new options being added
      const newFromJson = [...jsonOptions].filter(o => !localOptions.has(o));
      const newFromLocal = [...localOptions].filter(o => !jsonOptions.has(o));
      
      if (newFromJson.length > 0) {
        logger.debug(`  📥 "${categoryName}": Adding from JSON file: ${newFromJson.join(', ')}`);
      }
      if (newFromLocal.length > 0) {
        logger.debug(`  💾 "${categoryName}": Keeping localStorage options: ${newFromLocal.join(', ')}`);
      }
      
      merged.categories[categoryName] = {
        type: jsonCat.type || localCat.type,
        description: jsonCat.description || localCat.description,
        options: mergedOptions
      };
    } else if (jsonCat) {
      // Category only in JSON file - add it
      logger.debug(`  📥 New category from JSON file: "${categoryName}"`);
      merged.categories[categoryName] = jsonCat;
    } else if (localCat) {
      // Category only in localStorage - keep it
      logger.debug(`  💾 Keeping localStorage category: "${categoryName}"`);
      merged.categories[categoryName] = localCat;
    }
  }
  
  return merged;
}

/**
 * Get just the categories from taxonomy
 */
export async function getTaxonomyCategories(): Promise<{
  [key: string]: {
    type: string;
    description?: string;
    options: string[];
  };
}> {
  const taxonomy = await loadTaxonomy();
  return taxonomy.categories;
}

/**
 * Get subcategory map from taxonomy
 */
export async function getSubcategoryMap(): Promise<{
  [key: string]: string[];
}> {
  const taxonomy = await loadTaxonomy();
  return taxonomy.subcategoryMap || {};
}

/**
 * Get program methodology from taxonomy
 */
export async function getProgramMethodology(): Promise<{
  [key: string]: string;
}> {
  const taxonomy = await loadTaxonomy();
  return taxonomy.programMethodology || {};
}

/**
 * Get category level colors
 */
export async function getCategoryColors(): Promise<{
  [key: string]: string;
}> {
  const taxonomy = await loadTaxonomy();
  return taxonomy.colors?.categoryLevelColors || {};
}

/**
 * Get option level colors
 */
export async function getOptionColors(): Promise<{
  [key: string]: string;
}> {
  const taxonomy = await loadTaxonomy();
  return taxonomy.colors?.optionColors || {};
}

/**
 * Clear all taxonomy cache
 */
export function clearAllTaxonomyCache(): void {
  cachedTaxonomy = null;
  cacheTimestamp = 0;
  mergeCheckedThisSession = false; // Reset merge check flag

  if (typeof window === 'undefined') return;

  // Clear localStorage keys that might have stale data
  try {
    localStorage.removeItem('drillTaxonomy_json');
    localStorage.removeItem('tagTree_json');
    localStorage.removeItem(MERGED_VERSION_KEY);
    logger.debug('✅ Cleared localStorage taxonomy data and merge tracking');
  } catch (e) {
    logger.error('Failed to clear localStorage:', e);
  }
}

/**
 * Refresh taxonomy cache - forces reload from JSON file
 */
export async function refreshTaxonomy(): Promise<TaxonomyData> {
  clearAllTaxonomyCache();
  return loadTaxonomy();
}

/**
 * Load taxonomy directly from JSON file, bypassing localStorage
 * Use this to get the canonical/factory default taxonomy
 */
export async function loadTaxonomyFromFile(): Promise<TaxonomyData> {
  const module = await import('@/config/RuleDoc-Drill-Taxonomy.json');
  return module.default as unknown as TaxonomyData;
}

/**
 * Reset taxonomy to factory defaults from RuleDoc-Drill-Taxonomy.json
 * Clears all localStorage modifications and reloads from the canonical JSON file
 */
export async function resetTaxonomyToDefaults(): Promise<TaxonomyData> {
  // Clear all caches and localStorage
  clearAllTaxonomyCache();
  
  // Load directly from JSON file (canonical source)
  const module = await import('@/config/RuleDoc-Drill-Taxonomy.json');
  const json = module.default as unknown as TaxonomyData;
  
  // Save the JSON file version as merged version (so future merges know the baseline)
  saveMergedVersion(json.version);
  
  // Update cache
  cachedTaxonomy = json;
  cacheTimestamp = Date.now();
  
  logger.debug('✅ Taxonomy reset to factory defaults from RuleDoc-Drill-Taxonomy.json');
  return json;
}

/**
 * Force a merge check on next taxonomy load
 * Useful when the JSON file has been updated and you want to merge changes
 */
export function forceMergeCheck(): void {
  mergeCheckedThisSession = false;
  cachedTaxonomy = null;
  cacheTimestamp = 0;
  
  // Clear the merged version to trigger a merge
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(MERGED_VERSION_KEY);
    } catch (e) {
      logger.error('Failed to clear merged version:', e);
    }
  }
  
  logger.debug('🔄 Merge check will be triggered on next taxonomy load');
}

